/**
 * 매칭 테스트 모달 (M13 / C-2).
 *
 * 운영자가 임의의 OCR/자유 텍스트를 입력하면 백엔드의 AgentDetectionService 가
 * 실제 파이프라인과 동일하게 매칭을 수행한 결과를 반환한다.
 *
 * 활용 시나리오:
 *   - 새 agent 의 keywords 가 OCR 본문에 매칭되는지 사전 확인
 *   - priority 변경 후 동일 텍스트의 best_match 가 바뀌는지 검증
 *   - "왜 79999(UNKNOWN) 로 처리됐는지" 트러블슈팅 (해당 OCR 텍스트를 붙여넣고 시험)
 */

import { useEffect, useState } from "react";
import { Loader2, Play, Trophy, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/cn";

import { useMatchTest } from "./useAgents";

const TEXT_MAX = 200_000;

interface Props {
  onClose: () => void;
  /** 결과의 agent id 를 클릭했을 때 호출. (예: AgentsPage 에서 편집 모달 오픈) */
  onSelectAgent?: (agentId: string) => void;
}

export default function MatchTestModal({ onClose, onSelectAgent }: Props) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const mutation = useMatchTest();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const apiError =
    mutation.error instanceof ApiError ? mutation.error.detail : (mutation.error?.message ?? null);

  const canRun = text.trim().length > 0 && !mutation.isPending;

  const handleRun = () => {
    if (!canRun) return;
    mutation.mutate(text);
  };

  const result = mutation.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            {t("admin.agents.matchTest.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
          <p className="text-xs text-slate-500">{t("admin.agents.matchTest.description")}</p>

          <div>
            <label htmlFor="mt-text" className="block text-xs font-medium text-slate-700">
              {t("admin.agents.matchTest.textLabel")}
            </label>
            <textarea
              id="mt-text"
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={TEXT_MAX}
              placeholder={t("admin.agents.matchTest.textPlaceholder")}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={mutation.isPending}
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {t("admin.agents.matchTest.textHint", { count: text.length, max: TEXT_MAX })}
            </p>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleRun}
              disabled={!canRun}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {mutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              {t("admin.agents.matchTest.run")}
            </button>
          </div>

          {apiError ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {apiError}
            </div>
          ) : null}

          {result ? (
            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/40 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  {t("admin.agents.matchTest.resultTitle", {
                    count: result.candidates.length,
                  })}
                </span>
                {result.best_match_id ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                    <Trophy className="h-3 w-3" />
                    {t("admin.agents.matchTest.bestMatch", { id: result.best_match_id })}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                    {t("admin.agents.matchTest.noMatch")}
                  </span>
                )}
              </div>

              {result.candidates.length === 0 ? (
                <p className="text-[11px] italic text-slate-500">
                  {t("admin.agents.matchTest.noCandidates")}
                </p>
              ) : (
                <ul className="divide-y divide-slate-200 overflow-hidden rounded-md border border-slate-200 bg-white text-xs">
                  {result.candidates.map((c, idx) => {
                    const isBest = c.id === result.best_match_id;
                    return (
                      <li
                        key={c.id}
                        className={cn(
                          "flex items-center justify-between px-3 py-2",
                          isBest && "bg-blue-50/60",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-slate-400">#{idx + 1}</span>
                          <button
                            type="button"
                            onClick={() => onSelectAgent?.(c.id)}
                            disabled={!onSelectAgent}
                            className="font-mono font-medium text-slate-800 transition hover:text-blue-600 disabled:cursor-default disabled:hover:text-slate-800"
                            title={
                              onSelectAgent
                                ? t("admin.agents.matchTest.openAgent", { id: c.id })
                                : undefined
                            }
                          >
                            {c.id}
                          </button>
                          <span className="text-slate-700">{c.official_name}</span>
                          {c.currency ? (
                            <span className="font-mono text-[10px] text-slate-500">
                              {c.currency}
                            </span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-3 text-[11px]">
                          <span
                            className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-700"
                            title={t("admin.agents.matchTest.matchedKeywordTitle")}
                          >
                            {c.matched_keyword}
                          </span>
                          <span className="tabular-nums text-slate-500">
                            {t("admin.agents.matchTest.priorityShort", { priority: c.priority })}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t("common.actions.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
